import { useState } from 'react';
import type { ReactElement } from 'react';
import { assetToast, formatDate } from '@xdocs/i18n';
import type { DocumentVersion } from '@xdocs/schemas';
import { Badge, Button, ConfirmDialog, EmptyState, Skeleton, cn, useI18n, useToast } from '@xdocs/ui';
import { optionalProp } from '../../lib/optionalProp.js';
import { useEditorT } from './useEditorT.js';
import { useVersionHistory } from './useVersionHistory.js';
import styles from './VersionHistorySidebar.module.css';

export interface VersionHistorySidebarProps {
  documentId: string;
  /** Required for asset-named toasts/confirms (§16.1). */
  documentTitle: string;
  currentVersionId: string;
  onRestored: (newVersion: DocumentVersion) => void;
  className?: string | undefined;
}

/**
 * F67/F65's version-history sidebar (§28.1). Server-side paginated
 * `version.list` (E6/§5.9), "restore as new version" (composed from
 * `version.get` + `editor.save` — see `useVersionHistory`'s doc comment).
 * E19: loading (skeleton) · empty · error (retry) · populated, all four.
 */
export function VersionHistorySidebar({ documentId, documentTitle, currentVersionId, onRestored, className }: VersionHistorySidebarProps): ReactElement {
  const { t } = useEditorT();
  const { locale } = useI18n();
  const { push: pushToast } = useToast();
  const { versions, isLoading, isError, refetch, hasMore, loadMore, isLoadingMore, restoreAsNew, isRestoring } =
    useVersionHistory(documentId);
  const [confirmTarget, setConfirmTarget] = useState<DocumentVersion | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  async function handleRestoreConfirmed(): Promise<void> {
    if (!confirmTarget) return;
    setRestoreError(null);
    try {
      const restored = await restoreAsNew(confirmTarget.id, currentVersionId);
      onRestored(restored);
      pushToast(assetToast(locale, 'success', t('editor:toast.versionRestored.action'), documentTitle));
      setConfirmTarget(null);
    } catch (err) {
      const alert = err as { message?: string };
      setRestoreError(alert.message ?? 'errors:code.INTERNAL');
    }
  }

  return (
    <div className={cn(styles['sidebar'], className)}>
      <h2 className={styles['title']}>{t('editor:versions.title')}</h2>

      {isLoading && (
        <div aria-busy="true">
          <Skeleton shape="row" count={4} />
        </div>
      )}

      {isError && (
        <EmptyState
          kind="error"
          title={t('editor:error.generic.title')}
          action={{ label: t('editor:error.generic.retry'), onClick: refetch }}
        />
      )}

      {!isLoading && !isError && versions.length === 0 && <p className={styles['error']}>{t('editor:versions.empty')}</p>}

      {!isLoading && !isError && versions.length > 0 && (
        <>
          <ul className={styles['list']}>
            {versions.map((version) => {
              const isCurrent = version.id === currentVersionId;
              return (
                <li key={version.id} className={styles['row']} data-current={isCurrent}>
                  <div className={styles['rowHeader']}>
                    <span className={styles['origin']}>{t(`editor:versions.origin.${version.origin}`)}</span>
                    {isCurrent && <Badge tone="info">{t('editor:versions.current')}</Badge>}
                  </div>
                  <span className={styles['time']}>{formatDate(locale, version.createdAt, { hour: '2-digit', minute: '2-digit' })}</span>
                  {!isCurrent && (
                    <Button variant="ghost" size="sm" className={styles['restoreButton']} onClick={() => setConfirmTarget(version)}>
                      {t('editor:versions.restoreAsNew')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {hasMore && (
            <Button variant="ghost" size="sm" state={isLoadingMore ? 'loading' : 'idle'} onClick={loadMore}>
              {t('editor:versions.loadMore')}
            </Button>
          )}
        </>
      )}

      {confirmTarget && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmTarget(null);
          }}
          action={t('editor:versions.restoreAsNew')}
          assetName={documentTitle}
          onConfirm={handleRestoreConfirmed}
          loading={isRestoring}
          {...optionalProp('error', restoreError ?? undefined)}
        />
      )}
    </div>
  );
}
